import { LoaderFunction, useFetcher } from "react-router-dom";
import {
  getCampuses,
  getCourses,
  getDepartments,
  getSections,
  getTerms,
  Term,
} from "../../../../common/api/decimal/methods/verba.ts";
import { DecimalResult } from "../../../../common/api/decimal/utils.ts";
import { CommonResult } from "./types.ts";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { isTermReadOnly } from "../../search/course-browse/browseUtils.ts";

export const coursePickerLoader: LoaderFunction = ({
  request,
  params,
}): Promise<DecimalResult<CommonResult[]>> => {
  const url = new URL(request.url);

  const { campus } = params;

  if (url.href.includes("campus") && !campus) {
    return getCampuses();
  }

  const { term, department, course } = params;

  if (course) {
    return getSections(term!, department!, course, campus);
  }

  if (department) {
    return getCourses(term!, department, campus);
  }

  if (term) {
    return getDepartments(term, campus);
  }

  return getTerms(campus);
};

export const useCoursePickerFetcher = () => {
  return useFetcher<DecimalResult<CommonResult[]>>();
};

export const defaultCampusId = "__defaultCampusId__";

export const useCampusPickerFetcherWithDefault = () => {
  const intl = useIntl();
  const campusFetcher = useCoursePickerFetcher();
  const defaultCampusName = intl.formatMessage({ defaultMessage: "Default" });

  return useMemo(() => {
    const original = campusFetcher;
    const originalData = original.data;
    const defaultCampusOption = [
      { name: defaultCampusName, id: defaultCampusId },
    ];

    if (!originalData?.ok) {
      return original;
    }

    const list = originalData.data ?? [];

    if (list.length === 0) {
      // requirement is to tolerate campuses enabled brand flag true
      // but there are actually no campuses in the tenant
      return {
        ...original,
        data: {
          ...originalData,
          data: defaultCampusOption,
        },
      };
    }

    return original;
  }, [campusFetcher, defaultCampusName]);
};

/**
 * A fetcher hook for terms that adds "(Read Only)" suffix to inactive but browsable terms.
 */
export const useTermPickerFetcher = () => {
  const intl = useIntl();
  const termFetcher = useCoursePickerFetcher();
  const readOnlySuffix = intl.formatMessage({ defaultMessage: "(Read Only)" });

  return useMemo(() => {
    const original = termFetcher;
    const originalData = original.data;

    if (!originalData?.ok || !originalData.data) {
      return original;
    }

    return {
      ...original,
      data: {
        ...originalData,
        data: originalData.data.map((item) => {
          const term = item as unknown as Term;
          if (isTermReadOnly(term)) {
            return { ...item, name: `${item.name} ${readOnlySuffix}` };
          }
          return item;
        }),
      },
    };
  }, [termFetcher, readOnlySuffix]);
};
